// SPDX-License-Identifier: AGPL-3.0-or-later
import { ExtensionUrl, findExtension } from '@intakeform/fhir';
/**
 * Date-of-birth sanity checks (in-3uo).
 *
 * Triggered by `ExtensionUrl.dateMode` carrying `valueString='date-of-birth'`
 * on a `date` item. Rules:
 *
 * - Future dates → `error` (code: `dob-future`). The patient cannot be
 *   born tomorrow; this is almost always a typo (e.g. wrong year).
 * - Age > 120 → `warning` (code: `dob-implausible`). Soft because a
 *   small number of supercentenarians exist, but the form should
 *   prompt the patient to re-check.
 *
 * Returns an empty array when the item isn't a DOB-mode date, when no
 * answer is present, or when the answer isn't a parseable YYYY-MM-DD.
 * Other validators handle missing / type-mismatched answers; this
 * pass only fires once the basic shape is OK.
 */
const MAX_PLAUSIBLE_AGE_YEARS = 120;
export function isDateOfBirthItem(item) {
    if (item.type !== 'date')
        return false;
    const ext = findExtension(item, ExtensionUrl.dateMode);
    return ext?.valueString === 'date-of-birth';
}
/**
 * Years between `value` and `now`, rounded down (i.e. integer age on
 * the `now` date). FHIR `date` is `YYYY-MM-DD` UTC midnight; we use
 * UTC throughout to avoid the off-by-one timezone drift that breaks
 * birthdays around midnight.
 */
export function ageAt(value, now) {
    let years = now.getUTCFullYear() - value.getUTCFullYear();
    const monthDelta = now.getUTCMonth() - value.getUTCMonth();
    const dayDelta = now.getUTCDate() - value.getUTCDate();
    if (monthDelta < 0 || (monthDelta === 0 && dayDelta < 0))
        years -= 1;
    return years;
}
/** Strict parse of a FHIR `date` (YYYY-MM-DD). Returns null on any drift. */
export function parseFhirDate(raw) {
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(raw))
        return null;
    const d = new Date(`${raw}T00:00:00Z`);
    if (Number.isNaN(d.getTime()))
        return null;
    // Reject e.g. 2026-02-31 which Date silently rolls forward to March 3.
    if (d.toISOString().slice(0, 10) !== raw)
        return null;
    return d;
}
/**
 * Run DOB checks on a single answer value. Returns findings to push into
 * the response. Callers should already have routed to this only for
 * items where `isDateOfBirthItem(item)` is true.
 */
export function runDobChecks(item, rawAnswer, now) {
    const dob = parseFhirDate(rawAnswer);
    if (dob === null)
        return [];
    if (dob.getTime() > now.getTime()) {
        return [
            {
                linkId: item.linkId,
                severity: 'error',
                code: 'dob-future',
                message: 'Date of birth cannot be in the future.',
                i18nKey: 'finding.dob-future',
            },
        ];
    }
    const age = ageAt(dob, now);
    if (age > MAX_PLAUSIBLE_AGE_YEARS) {
        return [
            {
                linkId: item.linkId,
                severity: 'warning',
                code: 'dob-implausible',
                message: `Age of ${String(age)} years looks unusual — please check the date.`,
                // `age` is the interpolation argument the UI's translator
                // looks up by key — keep it stable as `age` to match the
                // chrome dictionary's `finding.dob-implausible` string.
                context: { age, ageYears: age, threshold: MAX_PLAUSIBLE_AGE_YEARS },
                i18nKey: 'finding.dob-implausible',
            },
        ];
    }
    return [];
}
/**
 * Computes the integer age in years for a DOB-mode answer, or null if
 * the answer is missing/unparseable. Used by the API on submit to
 * populate `questionnaire_responses.subject_age_at_submit`.
 */
export function ageAtSubmit(rawAnswer, now) {
    if (rawAnswer === undefined || rawAnswer.length === 0)
        return null;
    const dob = parseFhirDate(rawAnswer);
    if (dob === null)
        return null;
    // Future DOBs round to a negative age. We still record it so the
    // dashboard can flag obviously-broken submissions; the validator
    // already surfaced the error.
    return ageAt(dob, now);
}
//# sourceMappingURL=dob.js.map